import CloseIcon from '@mui/icons-material/Close';
import { IconButton } from '@mui/material';
import { ThemeProvider as MuiThemeProvider, StyledEngineProvider } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { MaterialDesignContent, SnackbarProvider, closeSnackbar } from 'notistack';
import React from 'react';
import { useTheme } from 'stores/theme';
import styled, { ThemeProvider, createGlobalStyle } from 'styled-components';
import { EColors, TStyledTheme, bizlyStyledTheme, getColor, getStyledTheme } from 'theme';
import { materialUIThemeV5Fn } from 'theme-mui-v5';
import { Column } from 'ui';
import App from './App';

const fontStyleFn = ({ theme: { getFontStyle, EFonts } }: { theme: TStyledTheme }) => ({
    '& *': getFontStyle(EFonts.body),
    '& h1, & h2, & h3, & h4, & h5, & h6, & h7': getFontStyle(EFonts.heading),
});

const FontProvider = createGlobalStyle(fontStyleFn);

const BizlyFontProvider = styled(Column)`
    width: 100%;
    ${fontStyleFn}
`;

const ThemedMuiThemeProvider = styled((props: React.ComponentProps<typeof MuiThemeProvider>) => (
    <StyledEngineProvider injectFirst>
        <MuiThemeProvider {...props} />
    </StyledEngineProvider>
)).attrs(materialUIThemeV5Fn)``;

// Create styled variants of MaterialDesignContent for each variant type
const SuccessSnackbar = styled(MaterialDesignContent)`
    &.notistack-MuiContent-success {
        background-color: ${() => getColor(EColors.snackbarSuccess)};
    }
`;

const ErrorSnackbar = styled(MaterialDesignContent)`
    &.notistack-MuiContent-error {
        background-color: ${() => getColor(EColors.snackbarError)};
    }
`;

const InfoSnackbar = styled(MaterialDesignContent)`
    &.notistack-MuiContent-info {
        background-color: ${() => getColor(EColors.snackbarInfo)};
    }
`;

// Bizly versions of the styled snackbars
const BizlySuccessSnackbar = styled(MaterialDesignContent)`
    &.notistack-MuiContent-success {
        background-color: ${() => getColor(EColors.snackbarSuccess)};
    }
`;

const BizlyErrorSnackbar = styled(MaterialDesignContent)`
    &.notistack-MuiContent-error {
        background-color: ${() => getColor(EColors.snackbarError)};
    }
`;

const BizlyInfoSnackbar = styled(MaterialDesignContent)`
    &.notistack-MuiContent-info {
        background-color: ${() => getColor(EColors.snackbarInfo)};
    }
`;

export const ThemedSnackbarProvider = ({ children, isBizly }: { children?: React.ReactNode; isBizly?: boolean }) => {
    return (
        <SnackbarProvider
            Components={{
                success: isBizly ? BizlySuccessSnackbar : SuccessSnackbar,
                error: isBizly ? BizlyErrorSnackbar : ErrorSnackbar,
                info: isBizly ? BizlyInfoSnackbar : InfoSnackbar,
            }}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'center',
            }}
            autoHideDuration={3000}
            iconVariant={{ error: null }}
            action={snackbarId => (
                <IconButton
                    size="small"
                    aria-label="Dismiss notification"
                    onClick={() => closeSnackbar(snackbarId)}
                    sx={{ color: 'inherit' }}
                >
                    <CloseIcon fontSize="small" />
                </IconButton>
            )}
        >
            {children}
        </SnackbarProvider>
    );
};

export const BizlyThemeOverride = ({ children }: { children?: React.ReactNode }) => {
    return (
        <StyledEngineProvider injectFirst>
            <ThemeProvider theme={{ ...bizlyStyledTheme() }}>
                <BizlyFontProvider>
                    <ThemedMuiThemeProvider>
                        <StyledEngineProvider injectFirst>
                            <ThemedSnackbarProvider isBizly>{children}</ThemedSnackbarProvider>
                        </StyledEngineProvider>
                    </ThemedMuiThemeProvider>
                </BizlyFontProvider>
            </ThemeProvider>
        </StyledEngineProvider>
    );
};

export default function ThemedApp() {
    // subscribe to stores so that the page will re-render when users select a new color or languages
    useTheme();

    return (
        <StyledEngineProvider injectFirst>
            <ThemeProvider theme={{ ...getStyledTheme() }}>
                <FontProvider theme={getStyledTheme()} />
                <ThemedMuiThemeProvider>
                    <StyledEngineProvider injectFirst>
                        <ThemedSnackbarProvider>
                            {/* We use DateFns rather than AdapterMoment */}
                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                                <App />
                            </LocalizationProvider>
                        </ThemedSnackbarProvider>
                    </StyledEngineProvider>
                </ThemedMuiThemeProvider>
            </ThemeProvider>
        </StyledEngineProvider>
    );
}
